import type { AuthMethod } from "@defuse-protocol/internal-utils"
import { QuoteRequest } from "@defuse-protocol/one-click-sdk-typescript"
import { settings } from "@src/components/DefuseSDK/constants/settings"
import type { TokenPrices } from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import { getPriceForToken } from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import type { BalanceMapping } from "@src/components/DefuseSDK/features/machines/depositedBalanceMachine"
import type {
  BaseTokenInfo,
  SupportedChainName,
  TokenDeployment,
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import { isDirectHyperliquidUsdcToken } from "@src/components/DefuseSDK/utils/hyperliquid"
import { isSamePaymentFamily } from "@src/components/DefuseSDK/utils/paymentTokenSelection"
import {
  computeTotalBalanceDifferentDecimals,
  getAnyBaseTokenInfo,
} from "@src/components/DefuseSDK/utils/tokenUtils"
import { isNearIntentsNetwork } from "@src/shared/utils/networks"
import { isNearFullBalance } from "../utils/exactOutBudget"
import {
  type ExactInputAmount,
  resolveWithdrawQuoteInput,
} from "../utils/withdrawQuoteUtils"
import {
  type ParsedWithdrawRecipient,
  parsedWithdrawRecipientMatchesNetwork,
  resolveWithdrawQuoteRecipient,
} from "../utils/withdrawRecipient"
import type { WithdrawQuote1csInput } from "./backgroundWithdraw1csQuoterMachine"

// Must stay above the max expected direct Hyperliquid USDC withdraw fee.
// Current observed fee is ~0.3153 USDC.
const DIRECT_HYPERLIQUID_USDC_WITHDRAW_BUFFER_USD = 0.4

type WithdrawFormContext = {
  recipient: string
  parsedAmount: { amount: bigint; decimals: number } | null
  parsedRecipient: ParsedWithdrawRecipient | null
  parsedDestinationMemo?: string | null
  blockchain: SupportedChainName | "near_intents"
  tokenIn: TokenInfo
  paymentToken: TokenInfo
  tokenOut: BaseTokenInfo
  tokenOutDeployment: TokenDeployment
}

type QuotePrerequisitesInput = {
  formContext: WithdrawFormContext
  userAddress: string | null
  userChainType: AuthMethod | null
}

export function getQuotePrerequisites({
  formContext,
  userAddress,
  userChainType,
}: QuotePrerequisitesInput): {
  formContext: WithdrawFormContext
  userAddress: string
  userChainType: AuthMethod
} | null {
  if (
    formContext.parsedAmount == null ||
    formContext.parsedAmount.amount === 0n ||
    formContext.parsedRecipient == null ||
    !parsedWithdrawRecipientMatchesNetwork(
      formContext.parsedRecipient,
      isNearIntentsNetwork(formContext.blockchain)
    ) ||
    userAddress == null ||
    userChainType == null
  ) {
    return null
  }

  return { formContext, userAddress, userChainType }
}

export function resolveQuoteInput(params: {
  formContext: WithdrawFormContext
  balances: BalanceMapping
  tokenList: TokenInfo[]
  amountMode: QuoteRequest.swapType
  exactInputAmount?: ExactInputAmount | null
}): {
  tokenIn: BaseTokenInfo
  amount: { amount: bigint; decimals: number }
} | null {
  const { formContext, balances, tokenList, amountMode, exactInputAmount } =
    params
  if (formContext.parsedAmount == null) return null

  return resolveWithdrawQuoteInput({
    formContext,
    amount: formContext.parsedAmount,
    amountMode,
    balances,
    tokenList,
    exactInputAmount,
  })
}

type PlanWithdrawQuoteRequestInput = {
  formContext: WithdrawFormContext
  userAddress: string | null
  userChainType: AuthMethod | null
  tokenList: TokenInfo[]
  balances: BalanceMapping
  amountMode: QuoteRequest.swapType
  exactInputAmount?: ExactInputAmount | null
  slippageBasisPoints: number
  slippageExplicitlySet?: boolean
  isConfidential?: boolean
  tokenPrices?: TokenPrices
  balancesLoaded: boolean
  nowMs?: number
}

type QuotePlanResult =
  | { type: "skip" }
  | { type: "error"; reason: "INSUFFICIENT_BALANCE" }
  | { type: "fallback_exact_in" }
  | { type: "quote"; payload: WithdrawQuote1csInput }

function getUsdValue(
  value: TokenValue,
  token: TokenInfo,
  tokenPrices: TokenPrices
): number | null {
  const price = getPriceForToken(token, tokenPrices)
  if (price == null || price <= 0) return null
  return (Number(value.amount) / 10 ** value.decimals) * price
}

export function planWithdrawQuoteRequest(
  input: PlanWithdrawQuoteRequestInput
): QuotePlanResult {
  const prerequisites = getQuotePrerequisites({
    formContext: input.formContext,
    userAddress: input.userAddress,
    userChainType: input.userChainType,
  })
  if (prerequisites == null) return { type: "skip" }

  const { formContext, userAddress, userChainType } = prerequisites
  const parsedAmount = formContext.parsedAmount
  if (parsedAmount == null) return { type: "skip" }
  const isCrossAsset = !isSamePaymentFamily(
    formContext.paymentToken,
    formContext.tokenIn
  )
  const isCrossAssetExactOutput =
    isCrossAsset && input.amountMode === QuoteRequest.swapType.EXACT_OUTPUT
  const balancesLoaded = input.balancesLoaded === true

  const quoteInput =
    isCrossAssetExactOutput && !balancesLoaded
      ? {
          tokenIn: getAnyBaseTokenInfo(formContext.paymentToken),
          amount: parsedAmount,
        }
      : resolveQuoteInput({
          formContext,
          balances: input.balances,
          tokenList: input.tokenList,
          amountMode: input.amountMode,
          exactInputAmount: input.exactInputAmount,
        })
  if (quoteInput == null) {
    return { type: "error", reason: "INSUFFICIENT_BALANCE" }
  }
  const { tokenIn: resolvedTokenIn, amount: adjustedAmount } = quoteInput
  const sourceTokenForPaymentSourceCheck = resolvedTokenIn
  const paymentSourceBalance = balancesLoaded
    ? computeTotalBalanceDifferentDecimals(
        sourceTokenForPaymentSourceCheck,
        input.balances,
        { strict: false }
      )
    : null
  if (
    isCrossAssetExactOutput &&
    balancesLoaded &&
    (paymentSourceBalance == null || paymentSourceBalance.amount === 0n)
  ) {
    return { type: "error", reason: "INSUFFICIENT_BALANCE" }
  }

  if (isCrossAssetExactOutput && balancesLoaded && input.tokenPrices != null) {
    const tokenOutNeedsDirectUsdcBuffer = isDirectHyperliquidUsdcToken(
      formContext.tokenOut
    )
    const sourceUsd =
      paymentSourceBalance != null
        ? getUsdValue(
            paymentSourceBalance,
            sourceTokenForPaymentSourceCheck,
            input.tokenPrices
          )
        : null
    const destinationUsd = tokenOutNeedsDirectUsdcBuffer
      ? Number(parsedAmount.amount) / 10 ** parsedAmount.decimals
      : getUsdValue(parsedAmount, formContext.tokenIn, input.tokenPrices)

    if (sourceUsd != null && destinationUsd != null) {
      if (sourceUsd < destinationUsd) {
        return { type: "error", reason: "INSUFFICIENT_BALANCE" }
      }
      if (
        tokenOutNeedsDirectUsdcBuffer &&
        sourceUsd < destinationUsd + DIRECT_HYPERLIQUID_USDC_WITHDRAW_BUFFER_USD
      ) {
        return { type: "fallback_exact_in" }
      }
    }
  }

  if (
    isSamePaymentFamily(formContext.paymentToken, formContext.tokenIn) &&
    input.amountMode === QuoteRequest.swapType.EXACT_OUTPUT &&
    isNearFullBalance(
      adjustedAmount,
      computeTotalBalanceDifferentDecimals(resolvedTokenIn, input.balances),
      input.slippageBasisPoints
    )
  ) {
    return { type: "fallback_exact_in" }
  }

  const parsedRecipient = formContext.parsedRecipient
  if (parsedRecipient == null) return { type: "skip" }
  const { recipient, destinationChainName, isAurora } =
    resolveWithdrawQuoteRecipient({
      parsedRecipient,
      displayAddress: formContext.recipient,
      blockchain: formContext.blockchain,
      destinationChainName: formContext.tokenOutDeployment.chainName,
    })

  const nowMs = input.nowMs ?? Date.now()
  return {
    type: "quote",
    payload: {
      tokenIn: resolvedTokenIn,
      tokenOut: formContext.tokenOut,
      amount: adjustedAmount,
      swapType: input.amountMode,
      slippageBasisPoints: input.slippageBasisPoints,
      slippageExplicitlySet: input.slippageExplicitlySet,
      defuseUserId: userAddress,
      deadline: new Date(nowMs + settings.dryQuoteDeadlineMs).toISOString(),
      userAddress,
      userChainType,
      recipient,
      ...(formContext.parsedDestinationMemo
        ? { destinationMemo: formContext.parsedDestinationMemo }
        : {}),
      ...(isAurora
        ? {
            virtualChainRecipient:
              formContext.parsedRecipient?.kind === "destination"
                ? formContext.parsedRecipient.address
                : undefined,
          }
        : {}),
      destinationChainName,
      ...(input.isConfidential ? { isConfidential: input.isConfidential } : {}),
    },
  }
}

type RequestWithdrawQuoteInput = {
  backgroundQuoterRef: {
    send: (event: {
      type: "NEW_QUOTE_INPUT"
      params: WithdrawQuote1csInput
    }) => void
  } | null
  formContext: WithdrawFormContext
  userAddress: string | null
  userChainType: AuthMethod | null
  tokenList: TokenInfo[]
  balances: BalanceMapping
  amountMode: QuoteRequest.swapType
  exactInputAmount?: ExactInputAmount | null
  slippageBasisPoints: number
  slippageExplicitlySet?: boolean
  isConfidential?: boolean
  tokenPrices?: TokenPrices
  balancesLoaded: boolean
  onQuoteError: (reason: string) => void
  onFallbackExactIn: () => void
}

export function requestWithdrawQuote(input: RequestWithdrawQuoteInput): void {
  if (!input.backgroundQuoterRef) {
    input.onQuoteError("Unable to initialize quote engine. Please try again.")
    return
  }

  const quotePlan = planWithdrawQuoteRequest({
    formContext: input.formContext,
    userAddress: input.userAddress,
    userChainType: input.userChainType,
    tokenList: input.tokenList,
    balances: input.balances,
    amountMode: input.amountMode,
    exactInputAmount: input.exactInputAmount,
    slippageBasisPoints: input.slippageBasisPoints,
    slippageExplicitlySet: input.slippageExplicitlySet,
    isConfidential: input.isConfidential,
    tokenPrices: input.tokenPrices,
    balancesLoaded: input.balancesLoaded,
  })

  if (quotePlan.type === "skip") {
    return
  }
  if (quotePlan.type === "error") {
    input.onQuoteError(quotePlan.reason)
    return
  }
  if (quotePlan.type === "fallback_exact_in") {
    input.onFallbackExactIn()
    return
  }

  input.backgroundQuoterRef.send({
    type: "NEW_QUOTE_INPUT",
    params: quotePlan.payload,
  })
}
